import { inject } from '@angular/core';
import { RedirectFunction, Router, Routes, UrlMatcher } from '@angular/router';
import { ErrorPageComponent } from './views/pages/error-page/error-page.component';
import { AuthGuard } from './trash/shared/guard/auth.guard';
import { BaseComponent } from './views/layout/base/base.component';

const canonicalTermsAndConditionsPath = 'termsandconditions';

const termsAndConditionsCaseInsensitiveMatcher: UrlMatcher = (segments) => {
  if (segments.length !== 1) {
    return null;
  }

  const path = segments[0].path;

  return path !== canonicalTermsAndConditionsPath && path.toLowerCase() === canonicalTermsAndConditionsPath
    ? { consumed: segments }
    : null;
};

const redirectToCanonicalTermsAndConditions: RedirectFunction = ({ queryParams, fragment }) =>
  inject(Router).createUrlTree([`/${canonicalTermsAndConditionsPath}`], {
    queryParams,
    fragment: fragment ?? undefined
  });

export const routes: Routes = [
  {
    matcher: termsAndConditionsCaseInsensitiveMatcher,
    redirectTo: redirectToCanonicalTermsAndConditions
  },
  {
    path: 'termsandconditions',
    loadComponent: () =>
      import('./views/pages/terms-and-conditions-page/terms-and-conditions-page.component').then(
        (m) => m.TermsAndConditionsPageComponent
      )
  },
  {
    path: 'PrivacyPolicy',
    loadComponent: () =>
      import('./views/pages/privacy-policy-page/privacy-policy-page.component').then(
        (m) => m.PrivacyPolicyPageComponent
      )
  },
  {
    // The signed-in customer's own valet ticket. Declared ahead of the BaseComponent layout — and outside it —
    // for the same reason PKOFF sits outside: the details page renders its own app-header, and nesting it in
    // the layout would stack a second one and push the card down.
    path: 'valet-tickets/:id',
    data: {
      title: 'Valet Ticket'
    },
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./valet-management/valet-tickets/components/public-valet-ticket/public-valet-ticket.component').then(
        (m) => m.PublicValetTicketComponent
      )
  },
  {
    path: '',
    component: BaseComponent,
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      },
      {
        path: 'home',
        loadComponent: () => import('./views/home/home.component').then((m) => m.HomeComponent)
      },
      {
        path: 'subscribe',
        loadComponent: () =>
          import('./views/pages/subscribe-stepper/subscribe-stepper.component').then(
            (m) => m.SubscribeStepperComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'bookings',
        loadComponent: () => import('./views/pages/bookings/bookings.component').then((m) => m.BookingsComponent)
      },
      {
        path: 'bookings/details/:id',
        loadComponent: () =>
          import('./views/pages/booking-details/booking-details.component').then((m) => m.BookingDetailsComponent),
        canActivate: [AuthGuard]
      },
      {
        // An on-street order whose payment did not complete: it has no reservation yet, so it
        // opens by order id (see payment-callback.logic.ts).
        path: 'bookings/order-details/:orderId',
        loadComponent: () =>
          import('./views/pages/booking-order-details/booking-order-details.component').then(
            (m) => m.BookingOrderDetailsComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'create-booking',
        loadComponent: () =>
          import('./views/pages/create-booking-stepper/create-booking-stepper.component').then(
            (m) => m.CreateBookingStepperComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'booking-renew-extend-checkout/:id',
        loadComponent: () =>
          import('./views/pages/booking-renew-extend-checkout/booking-renew-extend-checkout.component').then(
            (m) => m.BookingRenewExtendCheckoutComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'subscriptions',
        loadComponent: () =>
          import('./subscriptions/components/subscriptions/subscriptions.component').then(
            (m) => m.SubscriptionsComponent
          )
      },
      {
        path: 'violations',
        loadComponent: () =>
          import('./customer-management/customer-profiles/components/violations/violations.component').then(
            (m) => m.ViolationsComponent
          )
      },
      {
        path: 'violations/:id',
        data: {
          loggedInUserViolation: true
        },
        loadComponent: () =>
          import(
            './views/pages/public-on-street/components/public-settle-violation/public-settle-violation.component'
          ).then((m) => m.PublicSettleViolationComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'subscriptions/details/:id',
        loadComponent: () =>
          import('./subscription-details/components/subscription-details/subscription-details.component').then(
            (m) => m.SubscriptionDetailsComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        // A subscription order whose payment did not complete: it has no subscription yet, so it
        // opens by order id (see payment-callback.logic.ts).
        path: 'subscriptions/order-details/:orderId',
        loadComponent: () =>
          import('./subscription-details/components/subscription-order-details/subscription-order-details.component').then(
            (m) => m.SubscriptionOrderDetailsComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'vehicles',
        loadComponent: () =>
          import('./vehicle-management/vehicles/components/vehicles/vehicles.component').then(
            (m) => m.VehiclesComponent
          )
      },
      {
        path: 'vehicles/vehicle-details',
        loadComponent: () =>
          import('./vehicle-details/components/vehicle-details/vehicle-details.component').then(
            (m) => m.VehicleDetailsComponent
          ),
        canActivate: [AuthGuard]
      },

      {
        path: 'payment',
        loadComponent: () =>
          import('./views/pages/subscribe-stepper/payment-successful/payment-successful.component').then(
            (m) => m.PaymentSuccessfulComponent
          )
      },
      {
        path: 'payment-successful',
        redirectTo: 'payment',
        pathMatch: 'full'
      },
      {
        // No AuthGuard on either callback route: the customer arrives from the provider's domain on
        // a cold page load - guests included - and bouncing them to login would lose the result of
        // a payment that may already have succeeded.
        //
        // Redirect providers (Tabby): the backend-configured return URLs
        // /payment/callback/{success|cancel|failure}?payment_id=... - keep this shape.
        path: 'payment/callback/:outcome',
        data: { mechanism: 'redirect' },
        loadComponent: () =>
          import('./views/pages/payment-callback/payment-callback.component').then(
            (m) => m.PaymentCallbackComponent
          )
      },
      {
        // Hosted-widget providers (Hyperpay): the return URL this app builds for the widget,
        // /payment/callback?configurationId=...&id=... (the provider appends `id`).
        path: 'payment/callback',
        data: { mechanism: 'hostedWidget' },
        loadComponent: () =>
          import('./views/pages/payment-callback/payment-callback.component').then(
            (m) => m.PaymentCallbackComponent
          )
      },
      {
        path: 'user-profile',
        loadComponent: () =>
          import('./views/pages/user-profile/user-profile.component').then((m) => m.UserProfileComponent)
      },
      {
        path: 'my-orders',
        loadComponent: () =>
          import('./customer-management/customer-profiles/components/my-orders/my-orders.component').then(
            (m) => m.MyOrdersComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'subscriptions/renew-checkout',
        loadComponent: () =>
          import('./subscriptions/components/subscriptions/renew-extend-checkout/renew-extend-checkout.component').then(
            (m) => m.RenewExtendCheckoutComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'subscriptions/extend-checkout',
        loadComponent: () =>
          import('./subscriptions/components/subscriptions/renew-extend-checkout/renew-extend-checkout.component').then(
            (m) => m.RenewExtendCheckoutComponent
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'my-wallet',
        loadComponent: () => import('./views/pages/my-wallet/my-wallet.component').then((m) => m.MyWalletComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'valet-tickets',
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./valet-management/valet-tickets/components/my-valet-tickets/my-valet-tickets.component').then(
            (m) => m.MyValetTicketsComponent
          )
      },
      {
        path: 'parking-tickets',
        loadComponent: () =>
          import('./customer-management/customer-profiles/components/parking-tickets/parking-tickets.component').then(
            (m) => m.ParkingTicketsComponent
          )
      }
    ]
  },
  {
    path: 'auth',
    loadChildren: () => import('./views/pages/auth/auth/auth.module').then((m) => m.AuthModule),
    data: { title: 'GENERAL.AUTH' }
  },
  {
    path: 'receipt-invoice/by-order/:orderId',
    data: {
      title: 'Resit Invoice',
      privateInvoiceByOrder: true
    },
    loadComponent: () =>
      import('./subscriptions/components/subscriptions/receipt-invoice/receipt-invoice.component').then(
        (m) => m.ReceiptInvoiceComponent
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'receipt-invoice/:id',
    data: {
      title: 'Resit Invoice'
    },
    children: [
      {
        path: '',
        data: {
          title: null
        },
        loadComponent: () =>
          import('./subscriptions/components/subscriptions/receipt-invoice/receipt-invoice.component').then(
            (m) => m.ReceiptInvoiceComponent
          ),
        canActivate: [AuthGuard]
      }
    ]
  },
  {
    // Valet guest page. Token-only by design: valet codes are sequential and this page can summon a car,
    // so there is deliberately no /VLT/:code sibling to match PKOFF.
    path: 'VLT/:token',
    data: {
      title: 'Valet Ticket'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './valet-management/valet-tickets/components/public-valet-ticket/public-valet-ticket.component'
          ).then((m) => m.PublicValetTicketComponent)
      },
      {
        path: 'invoice/:id',
        loadComponent: () =>
          import('./subscriptions/components/subscriptions/receipt-invoice/receipt-invoice.component').then(
            (m) => m.ReceiptInvoiceComponent
          )
      }
    ]
  },
  {
    path: 'PKOFF/:code',
    data: {
      title: 'Parking Ticket Details'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './customer-management/customer-profiles/components/parking-tickets/parking-ticket-details/parking-ticket-details.component'
          ).then((m) => m.ParkingTicketDetailsComponent)
      },
      {
        path: 'invoice/:id',
        loadComponent: () =>
          import('./subscriptions/components/subscriptions/receipt-invoice/receipt-invoice.component').then(
            (m) => m.ReceiptInvoiceComponent
          )
      }
    ]
  },
  {
    path: 'payment-successfully',
    loadComponent: () =>
      import('./views/pages/subscribe-stepper/payment-successful/payment-successful.component').then(
        (m) => m.PaymentSuccessfulComponent
      )
  },
  {
    path: 'sv/:id',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-settle-violation/public-settle-violation.component'
      ).then((m) => m.PublicSettleViolationComponent)
  },
  {
    path: 'checkout/:id/extend',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-on-street-reservation-extend/public-on-street-reservation-extend.component'
      ).then((m) => m.PublicOnStreetReservationExtendComponent)
  },
  {
    path: 'checkout/payment-result',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-on-street-payment-result/public-on-street-payment-result.component'
      ).then((m) => m.PublicOnStreetPaymentResultComponent)
  },
  {
    path: 'checkout/payment-success',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-on-street-payment-success/public-on-street-payment-success.component'
      ).then((m) => m.PublicOnStreetPaymentSuccessComponent)
  },
  {
    path: 'checkout/payment-failed',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-on-street-payment-failed/public-on-street-payment-failed.component'
      ).then((m) => m.PublicOnStreetPaymentFailedComponent)
  },
  {
    path: 'checkout/invoice/by-order/:orderId',
    data: {
      publicInvoice: true,
      publicInvoiceByOrder: true
    },
    loadComponent: () =>
      import('./subscriptions/components/subscriptions/receipt-invoice/receipt-invoice.component').then(
        (m) => m.ReceiptInvoiceComponent
      )
  },
  {
    path: 'checkout/invoice/:id',
    data: {
      publicInvoice: true
    },
    loadComponent: () =>
      import('./subscriptions/components/subscriptions/receipt-invoice/receipt-invoice.component').then(
        (m) => m.ReceiptInvoiceComponent
      )
  },
  {
    path: 'checkout/:qrcode',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-on-street-checkout/public-on-street-checkout.component'
      ).then((m) => m.PublicOnStreetCheckoutComponent)
  },
  {
    path: 'checkout',
    loadComponent: () =>
      import(
        './views/pages/public-on-street/components/public-on-street-checkout/public-on-street-checkout.component'
      ).then((m) => m.PublicOnStreetCheckoutComponent)
  },
  {
    path: '**',
    component: ErrorPageComponent,
    title: 'GENERAL.NOT_FOUND_PAGE'
  }
];
